import React, { ReactNode, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { isNotNullish } from 'shared/utils/type-utils';
import { useMediaQuery } from '@material-ui/core';
import { muiTheme } from 'shared/themes';
import { MoreInfoProps, MobileInfoWrapper, DesktopInfoWrapper, InfoChipPill } from './more-info';

type WrapperProps = Partial<MoreInfoProps> & {
  children: ReactNode;
  clickable?: boolean;
  onClick?: () => void;
  role?: string;
};

function determineWrapper({ isInfoChip, isMobile }: Record<string, boolean>): (props: WrapperProps) => JSX.Element {
  if (!isInfoChip) {
    return InfoChipPillWrapper as (props: WrapperProps) => JSX.Element;
  }
  if (isMobile) {
    return MobileInfoWrapper as (props: WrapperProps) => JSX.Element;
  }
  return DesktopInfoWrapper as (props: WrapperProps) => JSX.Element;
}

export const TEST_IDS = {
  infoChip: `info-chip`,
};

export type InfoChipProps = {
  highlight?: boolean;
  id: string;
  moreInfo?: MoreInfoProps;
  text: ReactNode;
  titleCase?: boolean;
  onClick?: () => void;
  clickable?: boolean;
};

export function InfoChip({
  id,
  highlight = false,
  moreInfo,
  text,
  titleCase = false,
  onClick,
  clickable = true,
}: InfoChipProps): JSX.Element {
  const isInfoChip = isNotNullish(moreInfo);
  const isMobile = useMediaQuery<typeof muiTheme>((theme) => theme.breakpoints.down(`sm`));

  const Wrapper = useMemo(() => determineWrapper({ isInfoChip, isMobile }), [isInfoChip, isMobile]);

  return (
    <Wrapper
      onClick={clickable ? onClick : undefined}
      role={id === 'strainType' ? 'link' : 'button'}
      clickable={clickable}
      {...(isInfoChip ? moreInfo : {})}
    >
      <InfoChipText
        titleCase={!!titleCase}
        highlight={!!highlight}
        isInfoChip={isInfoChip}
        clickable={clickable}
        data-testid={TEST_IDS.infoChip}
      >
        {text}
      </InfoChipText>
    </Wrapper>
  );
}

const highlightStyles = css`
  font-weight: bold;
`;
const titleCaseStyles = css`
  text-transform: capitalize;
`;

const InfoChipText = styled.span<{
  clickable?: boolean;
  highlight?: boolean;
  titleCase?: boolean;
  isInfoChip?: boolean;
}>`
  ${({ highlight }) => (highlight ? highlightStyles : null)}
  ${({ titleCase }) => (titleCase ? titleCaseStyles : null)}
  color: ${({ theme }) => theme.colors.grey[30]};
  font-size: 13px;
  padding: ${({ isInfoChip }) => (isInfoChip ? '14px 6px 14px 18px' : '14px 18px')};
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
`;

const InfoChipPillWrapper = styled(InfoChipPill)<{ clickable?: boolean }>(({ clickable, theme }) =>
  clickable
    ? {
        '&:hover': {
          backgroundColor: theme.colors.grey[80],
        },
      }
    : {}
);
